// Historial de tareas resueltas EN LÍNEA dentro del panel de Tareas del Home:
// qué se hizo/omitió, QUIÉN lo marcó y cuándo. Sustituye al HistoryModal
// (mismo criterio que TaskDetailInline: nada de modales en este bloque).
import { useEffect, useMemo, useState } from 'react';
import { Check, CircleSlash, Repeat, Camera } from 'lucide-react';
import { listTasks, Task } from './api';

function fmtDay(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtTime(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit' });
}

export default function HistoryInline() {
  const [tasks, setTasks] = useState<Task[] | null>(null);
  // F4: «Solo con foto» = la vista de EVIDENCIAS del panel (miniaturas de lo archivado).
  const [soloFoto, setSoloFoto] = useState(false);

  useEffect(() => {
    listTasks('all').then((r) => setTasks(r.data)).catch(() => setTasks([]));
  }, []);

  // Solo resueltas, más recientes primero, agrupadas por día.
  const groups = useMemo(() => {
    const resolved = (tasks || [])
      .filter((t) => t.status !== 'open' && t.done_at && (!soloFoto || t.photo_path))
      .sort((a, b) => (b.done_at || '').localeCompare(a.done_at || ''))
      .slice(0, 200);
    const byDay = new Map<string, Task[]>();
    for (const t of resolved) {
      const key = fmtDay(t.done_at);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(t);
    }
    return [...byDay.entries()];
  }, [tasks, soloFoto]);

  if (tasks === null) return <p className="py-4 text-center text-sm text-content-3">Cargando…</p>;

  const filtro = (
    <div className="mb-2 flex justify-end">
      <button onClick={() => setSoloFoto((v) => !v)} aria-pressed={soloFoto}
        title="Ver solo las tareas con foto de evidencia archivada"
        className={`flex items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-medium transition ${soloFoto ? 'border-accent/50 bg-accent/15 text-accent-ink' : 'border-line text-content-2 hover:bg-surface-2'}`}>
        <Camera size={12} /> Solo con foto
      </button>
    </div>
  );
  if (groups.length === 0) {
    return (<>{filtro}<p className="py-4 text-center text-sm text-content-3">
      {soloFoto ? 'Aún no hay evidencias con foto.' : 'Aún no hay tareas resueltas.'}</p></>);
  }

  return (
    <div className="-mr-1 min-h-0 max-h-96 flex-1 space-y-4 overflow-y-auto pr-1 [scrollbar-width:thin]">
      {filtro}
      {groups.map(([day, items]) => (
        <div key={day}>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-3">{day}</p>
          <ul className="space-y-1">
            {items.map((t) => (
              <li key={t.task_id} className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-surface-2">
                <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md ${t.status === 'done' ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
                  {t.status === 'done' ? <Check size={13} /> : <CircleSlash size={12} />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-content">
                    {t.template_id != null && <Repeat size={11} className="mr-1 inline text-content-3" />}
                    {t.title}
                  </p>
                  <p className="truncate text-[11px] text-content-3">
                    {t.status === 'done' ? 'Hecha' : 'Omitida'}
                    {t.done_by_name ? ` por ${t.done_by_name}` : ''}
                    {t.done_at ? ` · ${fmtTime(t.done_at)}` : ''}
                    {t.status === 'skipped' && t.skip_reason ? ` — "${t.skip_reason}"` : ''}
                  </p>
                </div>
                {t.photo_path && (
                  <a href={t.photo_path} target="_blank" rel="noreferrer" title="Ver evidencia completa">
                    <img src={t.photo_path} alt="Evidencia" className="h-9 w-9 rounded-md border border-line object-cover" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
